// Aviso de reaprovação ao salvar o orçamento (Rafael, 07/10).
//
// O servidor (OrcamentoController::assinaturasDasLinhas + diffLinhas) devolve para aprovação do
// Financeiro o plano APROVADO de todo fornecedor cuja linha mudou e manda e-mail aos responsáveis
// do evento. O aviso da tela tem de prever exatamente os mesmos fornecedores — antes ele comparava
// totais/forma de pagamento e ignorava linha nova (clone, "+ Subitem") e linha removida.
//
// Mesmos campos do servidor: produto, fornecedor, quem paga, moeda (e as de etapa, 08/10), quantidade/diária/unitário/
// serviço das 3 etapas e imposto (R$ e %). Observação, forma de pagamento e totais NÃO contam.

export const CAMPOS_ASSINATURA = [
    'quantidadePreEvento', 'diariaPreEvento', 'valorUnitarioPreEvento', 'servicoPreEvento',
    'quantidadeNegociado', 'diariaNegociado', 'valorUnitarioNegociado', 'servicoNegociado',
    'quantidadeFechamento', 'diariaFechamento', 'valorUnitarioFechamento', 'servicoFechamento',
    'imposto', 'impostoPorcentagemItem',
];

const idDe = (v) => (v && typeof v === 'object' ? (v.id ?? '') : (v ?? '')).toString();

// "1.234,56" (tela) e "1234.560000" (API) viram o mesmo número.
export function paraNumero(v) {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    let s = String(v).trim();
    if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
}

// Chaves de fornecedor afetadas pela linha (igual ao servidor): quem paga quando é um fornecedor,
// senão o fornecedor da linha; e a coluna fornecedor crua.
export function chavesFornecedorDaLinha(sub) {
    const fp = idDe(sub && sub.fornecedorpagamento);
    const forn = idDe(sub && sub.fornecedor);
    const chave = (fp !== '' && fp !== 'agencia' && fp !== 'cliente') ? fp : forn;
    return [...new Set([chave, forn].filter((c) => c !== ''))];
}

// Família que, sem fornecedor, não entra no valor da Previsão (OrcamentoPagamentoController::linhaCompoeValorDoFornecedor).
const FAMILIA_IMPOSTO = ['IMPOSTO', 'IMPOSTOS', 'IOF', 'OUTRAS TAXAS'];

function linha(sub, grupo) {
    const numeros = CAMPOS_ASSINATURA.map((c) => paraNumero(sub[c]).toFixed(4));
    const forns = chavesFornecedorDaLinha(sub);
    const nomeProduto = String((sub.produto && typeof sub.produto === 'object' && sub.produto.nome) || '').trim().toUpperCase();
    const produto = idDe(sub.produto);
    // Moeda por etapa (08/10): igual ao servidor — com moeda de Negociado/Fechamento, "pré/neg/fech".
    const moedaNeg = idDe(sub.moedaNegociado);
    const moedaFech = idDe(sub.moedaFechamento);
    const moeda = idDe(sub.moeda) + (moedaNeg !== '' || moedaFech !== '' ? `/${moedaNeg}/${moedaFech}` : '');
    return {
        sig: [produto, idDe(sub.fornecedor), idDe(sub.fornecedorpagamento), moeda, ...numeros].join('|'),
        resto: [produto, moeda, ...numeros].join('|'),
        fornecedor: idDe(sub.fornecedor),
        quemPaga: idDe(sub.fornecedorpagamento).toLowerCase(),
        forns,
        // D-0096: chave do plano (quem paga, senão o fornecedor) e grupo da linha, como o servidor
        chave: forns[0] || '',
        grupo,
        // escolhido no select na mesma edição, o nome vem "codigo - [codigoFornecedor - ]NOME"
        impostoSemFornecedor: !forns[0] && FAMILIA_IMPOSTO.some((n) => nomeProduto === n || nomeProduto.endsWith(' - ' + n)),
        // Totais da linha por etapa — para dizer se o valor do fornecedor subiu ou desceu (07/10).
        tot: { pre: paraNumero(sub.totalPreEvento), neg: paraNumero(sub.totalNegociado), fech: paraNumero(sub.totalFechamento) },
    };
}

// Foto das linhas (subitens). Linha já gravada pelo id; linha nova pela chaveunica.
export function snapshotLinhas(dados) {
    const mapa = {};
    ((dados && dados.orcamentoitem) || []).forEach((grupo, gi) => {
        ((grupo && grupo.subitem) || []).forEach((sub, si) => {
            if (!sub) return;
            const chave = sub.id ? 'id:' + sub.id : 'novo:' + (sub.chaveunica || gi + '-' + si);
            mapa[chave] = linha(sub, idDe(grupo && grupo.id));
        });
    });
    return mapa;
}

// { alterados: Set, viraramCliente: Set } — fornecedores cujo plano aprovado o servidor vai reabrir.
// `previsao` (dadosDaPrevisao dos cards da Previsão) liga a regra do D-0096; sem ele, a de sempre.
export function fornecedoresAfetados(antes, agora, previsao = null) {
    const alterados = new Set();
    const viraramCliente = new Set();
    if (!antes || !agora) return { alterados, viraramCliente };
    const marcar = (lista) => lista.forEach((f) => alterados.add(f));
    const comLinhaQueVirouCliente = new Set();

    Object.keys(antes).forEach((k) => {
        const a = antes[k];
        const b = agora[k];
        if (!b) { marcar(a.forns); return; }               // linha removida
        if (a.sig === b.sig) return;                        // nada mudou
        if (a.quemPaga !== 'cliente' && b.quemPaga === 'cliente') {
            [...a.forns, ...b.forns].forEach((f) => comLinhaQueVirouCliente.add(f));
            return;
        }
        // Linha sem fornecedor que só RECEBEU o fornecedor ("Fornecedor a definir", 30/09): o servidor
        // não reabre o plano de quem recebeu (R6) — aqui também não avisa.
        if (!a.fornecedor && b.fornecedor && a.resto === b.resto) return;
        marcar(a.forns);
        marcar(b.forns);
    });
    Object.keys(agora).forEach((k) => {
        if (!antes[k]) marcar(agora[k].forns);              // linha nova (clone / "+ Subitem")
    });
    // D-0137: como o servidor, só "virou cliente" quem não ficou com nenhum item com valor na Agência (esse cancela as
    // parcelas). Fornecedor misto é valor alterado: a parte da Agência segue.
    const ficouComAgencia = (f) => Object.values(agora).some((l) => l.forns.includes(f) && l.quemPaga !== 'cliente'
        && (l.tot.pre !== 0 || l.tot.neg !== 0 || l.tot.fech !== 0));
    comLinhaQueVirouCliente.forEach((f) => (ficouComAgencia(f) ? alterados : viraramCliente).add(f));
    if (previsao) tirarTrocaDeGrupo(alterados, antes, agora, previsao);
    return { alterados, viraramCliente };
}

// D-0096 (Rafael 24/10) — o que a Previsão sabe e o aviso precisa: linhas que entram na Previsão (grupo bloqueado,
// como FEE, não entra); por fornecedor, o grupo de cada parcela de previsão e se ela já está em pagamento
// (liberada, paga, travada, com boleto ou com a NF do fornecedor recebida — essa não muda de fornecedor); o status
// e se o plano tem conta liberada ou não tem título (o servidor não mescla mantendo a aprovação); e os grupos com parcela
// e quem já recebeu linha do "Fornecedor a definir" (um plano só, mesmo status nos cards).
export function dadosDaPrevisao(cards) {
    const linhas = new Set();
    const parcelas = {};
    const planos = {};
    const aDefinir = { status: '', grupos: [], recebidos: [] };
    const vistas = new Set();
    (Array.isArray(cards) ? cards : []).forEach((card) => {
        (card.subitens_ids || []).forEach((id) => linhas.add(String(id)));
        const fid = String(card.fornecedor_real_id || card.fornecedor_id || '');
        const aprovadas = card.parcelas_aprovadas || [];
        if (card.a_definir || fid === '' || fid.indexOf('_a_definir') === 0) {
            if (card.status) aDefinir.status = String(card.status);
            aprovadas.forEach((p) => {
                const g = p.grupo_orcamentoitem_id ? String(p.grupo_orcamentoitem_id) : '';
                if (aDefinir.grupos.indexOf(g) < 0) aDefinir.grupos.push(g);
            });
            (card.fornecedores_recebidos || []).forEach((f) => { if (aDefinir.recebidos.indexOf(String(f)) < 0) aDefinir.recebidos.push(String(f)); });
            return;
        }
        // card por grupo: o plano é um só — junta o que os cards irmãos mostram
        const anterior = planos[fid] || { status: '', liberado: false, semTitulo: false };
        const contasEmAndamento = (card.parcelas_pagas || 0) + (card.parcelas_travadas || 0) + (card.parcelas_livres_pendentes || 0);
        planos[fid] = {
            status: String(card.status || anterior.status || ''),
            liberado: anterior.liberado || !!card.liberado_financeiro_at || contasEmAndamento > 0 || aprovadas.some((p) => !!p.liberado_financeiro_at),
            // sem título em Contas a Pagar e com parcela (previsão ou Efetivo): a mescla não mantém a aprovação. API sem o
            // campo: "não vai para Contas a Pagar".
            semTitulo: anterior.semTitulo || (card.tem_titulo === undefined
                ? (card.vai_contas_pagar === false && aprovadas.length > 0)
                : (card.tem_titulo === false && (aprovadas.length > 0 || (card.parcelas_efetivo || []).length > 0))),
        };
        aprovadas.forEach((p) => {
            if (vistas.has(String(p.id))) return;
            vistas.add(String(p.id));
            const emPagamento = !!(p.liberado_financeiro_at || p.pago || p.travado_pagamento || p.boleto_anexado);
            (parcelas[fid] = parcelas[fid] || []).push({
                grupo: p.grupo_orcamentoitem_id ? String(p.grupo_orcamentoitem_id) : '',
                // presa (fica com o fornecedor): com a NF recebida também — avisa a mais quem perde o grupo
                presa: emPagamento || !!card.nf_arquivo_id,
                // levada para quem recebe o grupo: só a em pagamento fica — avisa a mais quem recebe
                emPagamento,
            });
        });
    });
    return { linhas: [...linhas], parcelas, planos, aDefinir };
}

// Grupos que trocaram de fornecedor INTEIROS (antes só X, e talvez linhas sem fornecedor; depois só Y) — igual a
// FornecedorPorGrupo::gruposTrocados. { grupo: { de, para } }
export function gruposTrocados(antes, agora, compoe = () => true) {
    const porGrupo = (snap) => {
        const m = {};
        Object.keys(snap || {}).forEach((k) => {
            const l = snap[k];
            if (!l.grupo || !compoe(l)) return;
            (m[l.grupo] = m[l.grupo] || new Set()).add(l.chave);
        });
        return m;
    };
    const porGrupoAntes = porGrupo(antes);
    const porGrupoAgora = porGrupo(agora);
    const trocados = {};
    Object.keys(porGrupoAgora).forEach((g) => {
        const de = [...(porGrupoAntes[g] || [])].filter((c) => c !== '');
        const para = [...porGrupoAgora[g]];
        if (de.length !== 1 || para.length !== 1 || para[0] === '') return;
        if (de[0] !== para[0]) trocados[g] = { de: de[0], para: para[0] };
    });
    return trocados;
}

// Igual a FornecedorADefinir::filtrarSaidaSemParcela: não volta para aprovação X que só perdeu linhas (só o
// fornecedor mudou) de grupo em que o plano dele não tem parcela, nem Y que só recebeu um grupo inteiro (as parcelas
// vêm junto, com a aprovação) ou linha sem fornecedor. Os grupos das parcelas são os de DEPOIS do salvar: parcela sem
// grupo de quem estava num grupo só ganha esse grupo; a do grupo trocado vai para Y, menos a que está em pagamento.
function tirarTrocaDeGrupo(alterados, antes, agora, previsao) {
    const linhasPrevisao = new Set((previsao.linhas || []).map(String));
    const gruposPrevisao = new Set();
    const gruposPorChave = {};
    Object.keys(antes).forEach((k) => {
        const l = antes[k];
        if (k.indexOf('id:') === 0 && linhasPrevisao.has(k.slice(3))) gruposPrevisao.add(l.grupo);
        if (l.chave && l.grupo) (gruposPorChave[l.chave] = gruposPorChave[l.chave] || new Set()).add(l.grupo);
    });
    const compoe = (l) => !!l.grupo && l.quemPaga !== 'cliente' && !l.impostoSemFornecedor && gruposPrevisao.has(l.grupo);
    const trocados = gruposTrocados(antes, agora, compoe);

    const saidas = {};
    const entradas = new Set();
    const mudancaReal = new Set();
    const doADefinir = [];
    Object.keys(antes).forEach((k) => {
        const a = antes[k];
        const d = agora[k];
        if (d && a.sig === d.sig) return;
        const x = a.chave || '';
        const y = d ? (d.chave || '') : '';
        const soFornecedor = !!d && x !== y && a.resto === d.resto && a.grupo === d.grupo;
        if (!soFornecedor) {
            [...a.forns, ...(d ? d.forns : [])].forEach((f) => mudancaReal.add(f));
            return;
        }
        [...a.forns, ...d.forns].forEach((f) => { if (f !== x && f !== y) mudancaReal.add(f); });
        if (x !== '') (saidas[x] = saidas[x] || []).push(a.grupo);
        if (y !== '') {
            const t = trocados[a.grupo];
            if (x === '' || (t && t.de === x && t.para === y)) entradas.add(y); else mudancaReal.add(y);
            if (x === '') doADefinir.push({ fornecedor: y, grupo: a.grupo });
        }
    });
    Object.keys(agora).forEach((k) => { if (!antes[k]) agora[k].forns.forEach((f) => mudancaReal.add(f)); });

    const unicoAntes = (f) => (gruposPorChave[f] && gruposPorChave[f].size === 1 ? [...gruposPorChave[f]][0] : '');
    const gruposComParcela = (f) => {
        const unico = unicoAntes(f);
        const grupos = new Set();
        ((previsao.parcelas || {})[f] || []).forEach((p) => {
            const g = p.grupo || unico;
            const t = trocados[g];
            if (t && t.de === f && !p.presa) return;
            grupos.add(g);
        });
        return grupos;
    };
    [...alterados].forEach((f) => {
        if (mudancaReal.has(f) || (!saidas[f] && !entradas.has(f))) return;
        const grupos = gruposComParcela(f);
        if (!saidas[f] || (!grupos.has('') && !saidas[f].some((g) => grupos.has(g)))) alterados.delete(f);
    });

    // Opção A (Rafael 24/10): quem RECEBE parcelas que entram no plano dele sem manter a aprovação volta para
    // aprovação, e a tela avisa — como ADefinirPorGrupo::mesclaMantemAprovacao no servidor: só mantém com a origem e o
    // destino aprovados, o destino com título e sem conta liberada.
    const planos = previsao.planos || {};
    const aDefinir = previsao.aDefinir || { status: '', grupos: [], recebidos: [] };
    const gruposADefinir = new Set(aDefinir.grupos || []);
    const recebe = [];
    Object.keys(trocados).forEach((g) => {
        const { de, para } = trocados[g];
        const levadas = ((previsao.parcelas || {})[de] || []).some((p) => (p.grupo || unicoAntes(de)) === g && !p.emPagamento);
        if (levadas) recebe.push([para, (planos[de] || {}).status]);
    });
    // Herança do "a definir" por grupo (ADefinirPorGrupo::fornecedorHerdeiro): grupo com parcela "a definir" e sem linha
    // sem fornecedor; herda quem recebeu linha (neste Salvar ou antes) e está no grupo, se for um só; sem ninguém que
    // recebeu, o fornecedor único do grupo.
    const recebidos = new Set([...(aDefinir.recebidos || []), ...doADefinir.map((r) => r.fornecedor)]);
    gruposADefinir.forEach((g) => {
        if (!g) return;
        const chaves = new Set(Object.keys(agora).map((k) => agora[k]).filter((l) => l.grupo === g && compoe(l)).map((l) => l.chave || ''));
        if (chaves.size === 0 || chaves.has('')) return;
        const candidatos = [...chaves].filter((c) => recebidos.has(c));
        const herdeiro = candidatos.length === 1 ? candidatos[0] : (candidatos.length === 0 && chaves.size === 1 ? [...chaves][0] : null);
        if (herdeiro) recebe.push([herdeiro, aDefinir.status]);
    });
    // parcela "a definir" antiga, sem grupo: segue o plano inteiro para quem recebeu (avisa a mais)
    if (gruposADefinir.has('')) doADefinir.forEach(({ fornecedor }) => recebe.push([fornecedor, aDefinir.status]));
    recebe.forEach(([f, statusOrigem]) => {
        const plano = planos[f];
        if (plano && plano.status === 'aprovado' && (statusOrigem !== 'aprovado' || plano.liberado || plano.semTitulo)) alterados.add(f);
    });
}

// Valor vigente por fornecedor, como o servidor: última coluna preenchida no AGREGADO do fornecedor
// (Fechamento > Negociado > Pré-evento). Linha paga pelo cliente não conta. (Rafael 07/10)
export function valoresPorFornecedor(snapshot) {
    const soma = {};
    Object.keys(snapshot || {}).forEach((k) => {
        const l = snapshot[k];
        const chave = l.forns && l.forns[0];
        if (!chave || l.quemPaga === 'cliente' || !l.tot) return;
        const s = soma[chave] || (soma[chave] = { pre: 0, neg: 0, fech: 0 });
        s.pre += l.tot.pre; s.neg += l.tot.neg; s.fech += l.tot.fech;
    });
    const out = {};
    Object.keys(soma).forEach((k) => {
        const s = soma[k];
        out[k] = Math.round((s.fech > 0.005 ? s.fech : (s.neg > 0.005 ? s.neg : s.pre)) * 100) / 100;
    });
    return out;
}

// 'aumentou' | 'reduziu' | 'removido' | 'alterado' — o que o save faz com o valor do fornecedor.
export function variacaoDoFornecedor(antes, agora, fornId) {
    const a = valoresPorFornecedor(antes)[fornId] || 0;
    const d = valoresPorFornecedor(agora)[fornId] || 0;
    let tipo = 'alterado';
    if (d <= 0.005 && a > 0.005) tipo = 'removido';
    else if (d < a - 0.005) tipo = 'reduziu';
    else if (d > a + 0.005) tipo = 'aumentou';
    return { tipo, antes: a, depois: d };
}
